import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useEligibleAnnouncements } from '@/hooks/useAnnouncements';
import type { AudienceRules, PlatformAnnouncement } from '@/types/platformContent';
import { isAnnouncementEnvironmentAllowed, resolveAnnouncementEnvironment } from '@/lib/announcementEnvironments';

/** Rotas onde nunca exibimos comunicados (login, fluxos críticos). */
const SUPPRESSED_PREFIXES = [
  '/login',
  '/esqueci-senha',
  '/redefinir-senha',
  '/super-admin',
  '/crm/chat',
];

/** Atraso para garantir que o boot da aplicação nunca seja afetado. */
const START_DELAY_MS = 2000;

export const useAnnouncementQueue = () => {
  const { user } = useSupabaseAuth();
  const location = useLocation();
  const environment = resolveAnnouncementEnvironment(location.pathname);
  const [ready, setReady] = useState(false);

  const suppressed = SUPPRESSED_PREFIXES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (!user?.id) {
      setReady(false);
      return;
    }
    const timer = window.setTimeout(() => setReady(true), START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [user?.id]);

  const { data, refetch } = useEligibleAnnouncements(Boolean(user?.id) && ready, user?.id);

  // Ao sair de uma rota suprimida (ex.: Super Admin -> Início), revalida a fila.
  const prevSuppressed = useRef(suppressed);
  useEffect(() => {
    if (prevSuppressed.current && !suppressed && user?.id && ready) {
      refetch();
    }
    prevSuppressed.current = suppressed;
  }, [suppressed, user?.id, ready, refetch]);

  return useMemo(() => {
    const all = ((data || []) as PlatformAnnouncement[]).filter((announcement) =>
      isAnnouncementEnvironmentAllowed(announcement.audience_rules as AudienceRules | null, environment),
    );
    if (suppressed) {
      return { modal: null as PlatformAnnouncement | null, banners: [] as PlatformAnnouncement[] };
    }
    return {
      modal: all.find((a) => a.display_format === 'modal') || null,
      banners: all.filter((a) => a.display_format === 'banner').slice(0, 1),
    };
  }, [data, suppressed, environment]);
};
